import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { parseError } from '../utils/errorHandler';

export const TeamSelectionPage: React.FC = () => {
  const { createTeamWithSimpleAuth, joinTeamWithSimpleAuth } = useAuth();
  const { showError, showSuccess } = useToast();
  const [loading, setLoading] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [mode, setMode] = useState<'create' | 'join'>('create');

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamName.trim()) return;

    try {
      setLoading(true);
      console.log('🚀 Creating team:', teamName.trim());
      
      // 開発モード用の簡易チーム作成
      if (import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true') {
        showSuccess('チームを作成しました（開発モード）');
      } else {
        // 実際のAPI呼び出し
        if (createTeamWithSimpleAuth) {
          await createTeamWithSimpleAuth(teamName.trim());
          showSuccess('チームを作成しました');
        } else {
          throw new Error('チーム作成機能が利用できません');
        }
      }
    } catch (error) {
      console.error('❌ Team creation error:', error);
      const appError = parseError(error);
      showError(appError, 'チーム作成に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;

    try {
      setLoading(true);
      console.log('🚀 Joining team with code:', inviteCode.trim());
      
      // 開発モード用の簡易チーム参加
      if (import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true') {
        showSuccess('チームに参加しました（開発モード）');
      } else {
        // 実際のAPI呼び出し
        if (joinTeamWithSimpleAuth) {
          await joinTeamWithSimpleAuth(inviteCode.trim());
          showSuccess('チームに参加しました');
        } else {
          throw new Error('チーム参加機能が利用できません');
        }
      }
    } catch (error) {
      console.error('❌ Team join error:', error);
      const appError = parseError(error);
      showError(appError, 'チームへの参加に失敗しました。招待コードを確認してください');
    } finally {
      setLoading(false);
    }
  };



  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">処理中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100">
            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            チーム設定
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            新しいチームを作成するか、既存のチームに参加してください
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {/* モード切り替えタブ */}
          <div className="flex rounded-lg bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => setMode('create')}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                mode === 'create'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              チーム作成
            </button>
            <button
              type="button"
              onClick={() => setMode('join')}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                mode === 'join'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              チーム参加
            </button>
          </div>

          {/* チーム作成フォーム */}
          {mode === 'create' && (
            <form className="space-y-6" onSubmit={handleCreateTeam}>
              <div>
                <label htmlFor="teamName" className="block text-sm font-medium text-gray-700">
                  チーム名
                </label>
                <input
                  id="teamName"
                  name="teamName"
                  type="text"
                  required
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 focus:z-10 sm:text-sm"
                  placeholder="例：○○野球クラブ"
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading || !teamName.trim()}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  チームを作成
                </button>
              </div>
            </form>
          )}

          {/* チーム参加フォーム */}
          {mode === 'join' && (
            <form className="space-y-6" onSubmit={handleJoinTeam}>
              <div>
                <label htmlFor="inviteCode" className="block text-sm font-medium text-gray-700">
                  招待コード
                </label>
                <input
                  id="inviteCode"
                  name="inviteCode"
                  type="text"
                  required
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="招待コードを入力"
                />
                <p className="mt-1 text-xs text-gray-500">
                  チーム管理者から受け取った招待コードを入力してください
                </p>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading || !inviteCode.trim()}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  チームに参加
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamSelectionPage;